import React from 'react';
import './rush.css';
import './features.css';
import './feature-detail.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { FI, FeatHero, FeatCapabilities, FeatDetailRow, FeatSteps, FeatQuote, FeatCrossLinks, FeatFinalCTA } from './RushFeatureLayout';

const AUNode = ({ k, ic, title, sub, accent }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', borderRadius: 11, background: accent ? 'color-mix(in oklab, var(--indigo) 7%, var(--bg-card))' : 'var(--bg-soft)', border: `1px solid ${accent ? 'color-mix(in oklab, var(--indigo) 30%, var(--line))' : 'var(--line)'}` }}>
    <span style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--bg-card)', border: '1px solid var(--line)', color: accent ? 'var(--indigo)' : 'var(--ink-3)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>{ic}</span>
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.06em', color: 'var(--ink-4)' }}>{k}</span>
      <b style={{ fontSize: 13 }}>{title}</b>
      <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>{sub}</span>
    </div>
  </div>
);
const AUConnector = () => <div style={{ width: 1, height: 13, background: 'var(--line-2)', marginLeft: 28 }}></div>;

const AUHeroVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Localize on every PR · Active</span></div>
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 0 }}>
      <AUNode k="WHEN" ic={FI.flow} title="Pull request opened" sub="acme/strings" />
      <AUConnector />
      <div style={{ marginLeft: 28, marginBottom: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', background: 'var(--bg-soft)', border: '1px solid var(--line)', borderRadius: 999, padding: '3px 11px' }}>If file type is JSON</span>
      </div>
      <AUConnector />
      <AUNode k="THEN" ic={FI.ai} title="Translate" sub="into DE · FR · JA · ES" accent />
      <AUConnector />
      <AUNode k="THEN" ic={FI.flow} title="Open translation PR" sub="as a follow-up PR" accent />
      <div style={{ display: 'flex', gap: 14, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)', fontSize: 11.5, color: 'var(--ink-3)' }}>
        <span>Ran <b style={{ color: 'var(--ink)' }}>142</b> times</span>
        <span>·</span>
        <span>Saved <b style={{ color: 'var(--ink)' }}>38 hrs</b> this month</span>
        <span style={{ marginLeft: 'auto', color: 'var(--green)', fontWeight: 600 }}>99.4% success</span>
      </div>
    </div>
  </div>
);

const AURecipesVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Start from a recipe</span></div>
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[[FI.flow, 'Translate on every PR', 'Diff i18n files, open a translation PR back.'],
      [FI.layers, 'Auto-route by language', 'Send new files to the right linguist.'],
      [FI.bolt, 'Deliver when 100% done', "Export and notify the moment it's complete."],
      [FI.lock, 'Glossary guardrail', 'Block approvals that break locked terms.']].map(([ic, t, d]) => (
        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--bg-soft)' }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--indigo-soft)', color: 'var(--indigo)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>{ic}</span>
          <div style={{ minWidth: 0 }}>
            <b style={{ fontSize: 12.5, display: 'block' }}>{t}</b>
            <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>{d}</span>
          </div>
          <span style={{ marginLeft: 'auto', color: 'var(--ink-4)', flexShrink: 0 }}>{FI.arrowR}</span>
        </div>
      ))}
    </div>
  </div>
);

const RushFeatureAutomations = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="features" />
      <FeatHero
        tint="indigo"
        kicker="Automations"
        kickerIcon={FI.flow}
        title="Localization that runs itself."
        lead="Turn your connected sources into pipelines. When something happens — a PR opens, a file changes, a project hits 100% — Rush Studio runs the steps for you. This is the literal 'localization on every PR' promise, made real."
        primary={{ label: 'Get early access', to: '/register' }}
        secondary={{ label: 'See all features', to: '/features' }}
        meta={['6 event triggers', 'One-click recipes', 'Full run history']}
        visual={<AUHeroVisual />}
      />

      <FeatCapabilities
        tint="indigo"
        eyebrow="What it does"
        title="Build pipelines, not tickets."
        lead="A trigger, optional conditions, and a chain of actions. No code, no glue scripts — just the work that used to live in someone's head."
        caps={[
          { icon: FI.bolt, title: 'Event triggers', desc: 'PR opened, file added, project complete, deadline near, comment posted, or a schedule.' },
          { icon: FI.flow, title: 'Conditions & actions', desc: 'Filter by language or file type, then translate, route, notify, export, or open a PR.' },
          { icon: FI.layers, title: 'Recipe templates', desc: "Start from 'Translate on every PR' or 'Glossary guardrail' in a single click." },
          { icon: FI.history, title: 'Runs & audit', desc: 'Every run is logged with a success rate and the hours it saved your team.' },
        ]}
      />

      <FeatDetailRow
        reverse
        eyebrow="Recipes"
        title="Start from a proven pattern."
        intro="You don't have to design a pipeline from scratch. Pick a recipe, point it at a source, and you're live. Every recipe is just a starting point — open the builder and add conditions or actions any time."
        points={[
          { icon: FI.flow, title: 'Translate on every PR', desc: 'Diff your i18n files on each pull request and open a translation PR back automatically.' },
          { icon: FI.layers, title: 'Auto-route by language', desc: 'New files land with the right linguist the moment they arrive — no manual triage.' },
          { icon: FI.bolt, title: 'Deliver when 100% done', desc: 'Export and notify the second every language clears review. Hands off.' },
        ]}
        replaces={['Manual file shuffling', '"Did anyone translate this?" threads']}
        visual={<AURecipesVisual />}
      />

      <FeatSteps
        eyebrow="How it works"
        title="Three parts. Endless pipelines."
        steps={[
          { h: 'Pick a trigger', p: 'Choose the event that kicks things off — a PR, a new file, a completed project, or a schedule.' },
          { h: 'Add conditions & actions', p: 'Narrow by language or file type, then chain the steps: translate, route, notify, export, or open a PR.' },
          { h: 'Turn it on', p: "Save and it's live. Watch runs accrue in the audit log, with the hours saved tallied for you." },
        ]}
      />

      <FeatQuote quote="Our strings PR now opens its own translation PR. The localization step just isn't a manual task anymore." initials="TS" name="Tomás Silva" role="Staff engineer, Helios" />

      <FeatCrossLinks current="automations" />

      <FeatFinalCTA title="Put your localization on autopilot." sub="Join the waitlist for early-access pricing and a 1:1 onboarding call with our team." />

      <RushFooter />
    </div>
  );
};

export default RushFeatureAutomations;
